/**
 * Leviathan Web - Email Settings Page
 * Quasar Prime LLC
 *
 * Configure SMTP settings and email defaults
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail,
  Save,
  RefreshCw,
  Server,
  Shield,
  Send,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface EmailSettings {
  id: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  fromEmail: string;
  fromName: string;
  replyToEmail: string | null;
  ccEmail: string | null;
  bccEmail: string | null;
  isEnabled: boolean;
  dailyLimit: number | null;
  updatedAt: string;
}

export default function EmailSettingsPage() {
  const queryClient = useQueryClient();
  const { accessToken } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: true,
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    ccEmail: '',
    bccEmail: '',
    isEnabled: true,
    dailyLimit: 500,
  });

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };

  // Fetch existing settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['email-settings'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/email/settings`, { headers });
      if (!response.ok) throw new Error('Failed to fetch settings');
      return response.json() as Promise<EmailSettings | null>;
    },
    enabled: !!accessToken,
  });

  // Populate form with existing data
  useEffect(() => {
    if (settings) {
      setFormData({
        smtpHost: settings.smtpHost || '',
        smtpPort: settings.smtpPort || 587,
        smtpUser: settings.smtpUser || '',
        smtpPassword: '', // Don't show existing password
        smtpSecure: settings.smtpSecure ?? true,
        fromEmail: settings.fromEmail || '',
        fromName: settings.fromName || '',
        replyToEmail: settings.replyToEmail || '',
        ccEmail: settings.ccEmail || '',
        bccEmail: settings.bccEmail || '',
        isEnabled: settings.isEnabled ?? true,
        dailyLimit: settings.dailyLimit || 500,
      });
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Only send password if it was changed
      const payload = {
        ...data,
        smtpPassword: data.smtpPassword || undefined,
      };
      const response = await fetch(`${API_URL}/api/email/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to save settings');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-settings'] });
    },
  });

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-amber-500/20 border border-amber-500/30">
            <Mail className="h-5 w-5 text-amber-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Email Settings</h1>
            <p className="text-sm text-slate-400">
              Configure SMTP server and email defaults
            </p>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {saveMutation.isSuccess && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <span className="text-green-400">Settings saved successfully!</span>
        </div>
      )}

      {saveMutation.isError && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">
            Failed to save settings. Please try again.
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
        {/* Enable/Disable Toggle */}
        <div className="bg-slate-800/50 border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Email Service Status
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Enable or disable outgoing email functionality
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isEnabled}
                onChange={(e) =>
                  setFormData({ ...formData, isEnabled: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-amber-500"></div>
            </label>
          </div>
        </div>

        {/* SMTP Configuration */}
        <div className="bg-slate-800/50 border border-slate-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Server className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-white">
              SMTP Configuration
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                SMTP Host *
              </label>
              <input
                type="text"
                value={formData.smtpHost}
                onChange={(e) =>
                  setFormData({ ...formData, smtpHost: e.target.value })
                }
                placeholder="smtp.example.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                SMTP Port *
              </label>
              <input
                type="number"
                value={formData.smtpPort}
                onChange={(e) =>
                  setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })
                }
                placeholder="587"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                SMTP Username *
              </label>
              <input
                type="text"
                value={formData.smtpUser}
                onChange={(e) =>
                  setFormData({ ...formData, smtpUser: e.target.value })
                }
                placeholder="user@example.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                SMTP Password {settings?.smtpPassword ? '(leave blank to keep)' : '*'}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.smtpPassword}
                  onChange={(e) =>
                    setFormData({ ...formData, smtpPassword: e.target.value })
                  }
                  placeholder={settings?.smtpPassword ? '••••••••' : 'Enter password'}
                  className="w-full px-3 py-2 pr-10 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smtpSecure"
                  checked={formData.smtpSecure}
                  onChange={(e) =>
                    setFormData({ ...formData, smtpSecure: e.target.checked })
                  }
                  className="h-4 w-4 bg-slate-900 border-slate-600 text-amber-500 focus:ring-amber-500"
                />
                <label htmlFor="smtpSecure" className="text-sm text-slate-300">
                  Use TLS/SSL encryption
                </label>
              </div>

              {/* This button used to POST to /api/email/settings/test, a route
                  that has never been registered. It 404'd, the catch turned
                  that into a generic connection error, and the user was told
                  their SMTP credentials were wrong when in fact nothing had
                  been tested at all.

                  It is disabled rather than repointed because there is nothing
                  to point it at: no mail provider is wired on this product
                  (email.service.ts has DELIVERY_CONFIGURED = false), so a test
                  would have to verify a connection nothing will ever use. Doing
                  the honest thing here — say so — is better than either a
                  button that lies or a green tick that means nothing. */}
              <button
                type="button"
                disabled
                title="Email delivery is not wired on this instance, so there is no connection to test."
                className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-700 text-slate-500 cursor-not-allowed"
              >
                <Shield className="h-4 w-4" />
                Test Connection
              </button>

              <span className="flex items-center gap-1 text-amber-500/80 text-sm">
                <XCircle className="h-4 w-4" />
                Unavailable — no mail provider is configured on this instance, so
                these settings are stored but never used.
              </span>
            </div>
          </div>
        </div>

        {/* Default Email Settings */}
        <div className="bg-slate-800/50 border border-slate-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Send className="h-5 w-5 text-amber-500" />
            <h2 className="text-lg font-semibold text-white">
              Default Email Settings
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                From Email *
              </label>
              <input
                type="email"
                value={formData.fromEmail}
                onChange={(e) =>
                  setFormData({ ...formData, fromEmail: e.target.value })
                }
                placeholder="noreply@company.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                From Name *
              </label>
              <input
                type="text"
                value={formData.fromName}
                onChange={(e) =>
                  setFormData({ ...formData, fromName: e.target.value })
                }
                placeholder="Your Company Name"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Reply-To Email
              </label>
              <input
                type="email"
                value={formData.replyToEmail}
                onChange={(e) =>
                  setFormData({ ...formData, replyToEmail: e.target.value })
                }
                placeholder="support@company.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Recipients will reply to this address
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Default CC
              </label>
              <input
                type="email"
                value={formData.ccEmail}
                onChange={(e) =>
                  setFormData({ ...formData, ccEmail: e.target.value })
                }
                placeholder="accounting@company.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Optional CC for all outgoing emails
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Default BCC
              </label>
              <input
                type="email"
                value={formData.bccEmail}
                onChange={(e) =>
                  setFormData({ ...formData, bccEmail: e.target.value })
                }
                placeholder="records@company.com"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Hidden copy for record keeping
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Daily Limit
              </label>
              <input
                type="number"
                value={formData.dailyLimit}
                onChange={(e) =>
                  setFormData({ ...formData, dailyLimit: parseInt(e.target.value) || 0 })
                }
                placeholder="500"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
              />
              <p className="text-xs text-slate-500 mt-1">
                Maximum emails per day (0 = unlimited)
              </p>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-amber-500/10 border border-amber-500/30 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-amber-500 font-medium">Configuration Tips</h3>
              <ul className="mt-2 text-sm text-slate-400 space-y-1 list-disc list-inside">
                <li>
                  For Gmail: Use smtp.gmail.com port 587 with TLS. You'll need an
                  App Password if 2FA is enabled.
                </li>
                <li>
                  For Office 365: Use smtp.office365.com port 587 with TLS.
                </li>
                <li>
                  For Amazon SES: Use email-smtp.region.amazonaws.com port 587.
                </li>
                {/* Was: "Test your connection after saving to ensure emails
                    will be delivered." The Test Connection button above is
                    disabled precisely BECAUSE there is nothing to test, so
                    this instructed the admin to perform a check that cannot
                    happen and implied a delivery guarantee that does not
                    exist. The banner and the button had been made honest; this
                    line still said the old thing. */}
                <li>
                  These settings are stored but not used on this instance: no
                  mail provider is wired, so nothing is sent. Entering them
                  correctly will not cause email to be delivered.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
